import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { IconLock, IconPencil } from '@tabler/icons-react'
import { differenceInCalendarDays, format, isValid, parseISO, startOfWeek } from 'date-fns'
import { capitalize, isEmpty, isNil } from 'lodash'
import queryString from 'query-string'

import { FilterForm } from '@/components/filters/FilterForm'
import { FormField } from '@/components/forms/FormField'
import { PageHeader } from '@/components/headers/PageHeader'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import Select from '@/components/forms/Select'
import { AnalyticsCard } from '@/components/cards/AnalyticsCard'
import { Card } from '@/components/cards/Card'
import { CopyButton } from '@/components/buttons/CopyButton'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { EChart } from '@/components/charts/EChart'
import { CHART } from '@/components/charts/chartPalette'
import { Accordion } from '@/components/accordions/Accordion'
import { Progress } from '@/components/progress/Progress'
import { Pill } from '@/components/pills/Pill'
import { toast } from '@/components/toasts/toast.helpers'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { formatSeconds, getOrganizationOptions } from '@/utils/helpers'
import { fetchOrganizations } from '@/reducers/organizations/organizations.actions'
import { fetchUsers } from '@/reducers/organizations/organizationUsers.actions'
import { formatPolicyWindow, policyUsagePercent } from '@/views/Kodi/kodi.constants'
import {
  fetchKodiUsageStats,
  fetchKodiUsagePolicies,
  fetchKodiUsageSessions,
  fetchKodiUsageSessionsByDay,
  fetchKodiNegativeFeedback,
} from '@/reducers/kodi/kodi.actions'
import { CreateUsagePolicyForm } from './CreateUsagePolicyForm'

import './KodiUsagePage.scss'

// Mirrors the backend's MAX_RANGE_DAYS on /usage-stats: both reject a
// start→end date difference of 60+, i.e. allow at most 60 inclusive calendar
// days (a 59-day difference — the "Last 60 Days" preset).
const MAX_RANGE_DAYS = 60

const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/

// The linkable range from ?from_date/?to_date, or null unless both are
// yyyy-MM-dd, in order, no later than today, and inside MAX_RANGE_DAYS — the
// same limits the picker and backend enforce — so a bad link never reaches
// the backend.
const parseUrlRange = (fromDate, toDate, today) => {
  if (!DATE_PARAM_PATTERN.test(fromDate) || !DATE_PARAM_PATTERN.test(toDate)) {
    return null
  }
  const start = parseISO(fromDate)
  const end = parseISO(toDate)
  const days = differenceInCalendarDays(end, start)
  return isValid(start) &&
    isValid(end) &&
    days >= 0 &&
    days < MAX_RANGE_DAYS &&
    differenceInCalendarDays(end, today) <= 0
    ? { start, end }
    : null
}

// Viewer's IANA zone so the backend buckets the picked days as local calendar
// days, not UTC. Detected once; UTC fallback for runtimes that can't resolve a
// zone. The picker's dates are already local, so this just tells the backend how
// to read them.
const browserTimezone = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
})()

// Session table date cell: shows the date, with the full local datetime on hover
// (the API sends UTC-offset timestamps, so new Date renders them in local time).
// Guards a missing value so one bad row can't throw and crash the whole table.
const dateCell = (value) =>
  value ? (
    <InteractiveTooltip content={format(new Date(value), 'MMM d, yyyy h:mm a')}>
      <span>{format(new Date(value), 'MMM d, yyyy')}</span>
    </InteractiveTooltip>
  ) : (
    ''
  )

// Session UUID cell: the id plus a compact copy button (shared component).
const sessionUuidCell = (sessionId) => (
  <span className="flex-align-center small-gap">
    {sessionId}
    <CopyButton content={sessionId} iconOnly basic showTooltip tooltipContent="Copy session UUID" />
  </span>
)

export const KodiUsagePage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const organizations = useSelector((state) => state.organizations)

  // The org filter lives in the URL (?org_id=<id>) so it's linkable and a
  // param on load takes effect immediately. No param (or a cleared select) =
  // null = all organizations. Org ids are positive; anything else (junk, 0,
  // negatives) falls back to the all-orgs view instead of rendering an
  // org-scoped page for a nonexistent org.
  const searchParams = queryString.parse(location.search)
  const rawOrgId = searchParams.org_id
  const numericOrgId = Number(rawOrgId)
  const selectedOrgId =
    rawOrgId && Number.isInteger(numericOrgId) && numericOrgId >= 1 ? numericOrgId : null

  // Unredacted orgs hide session titles (the backend returns a placeholder) and
  // their rows aren't linkable; a lock badge flags the state near the filters.
  const selectedOrg = organizations.find((org) => org.id === selectedOrgId)
  const isUnredacted = Boolean(selectedOrg?.unredacted_enabled)

  const handleOrgChange = (orgId) => {
    history.push({
      pathname: location.pathname,
      search: queryString.stringify({ ...searchParams, org_id: isNil(orgId) ? undefined : orgId }),
    })
  }

  // The URL is the source of truth for the committed range (?from_date=&to_date=
  // as yyyy-MM-dd), so links and Back/Forward work. Missing or invalid params
  // mean this week to date. The picker keeps a pending range because
  // react-date-range emits mid-pick; it commits to the URL only when the popup
  // closes — one backend query per finished selection, not one per click. That
  // commit replaces the history entry, so Back steps through org changes, not
  // every range change. "Today" is fixed at mount so a tab left open past
  // midnight doesn't shift the default range on the next render.
  const [today] = useState(() => new Date())
  const urlRange = parseUrlRange(searchParams.from_date, searchParams.to_date, today)
  const committedStartDate = urlRange?.start ?? startOfWeek(today, { weekStartsOn: 1 })
  const committedEndDate = urlRange?.end ?? today
  const committedStart = format(committedStartDate, 'yyyy-MM-dd')
  const committedEnd = format(committedEndDate, 'yyyy-MM-dd')
  const [pendingStartDate, setPendingStartDate] = useState(committedStartDate)
  const [pendingEndDate, setPendingEndDate] = useState(committedEndDate)

  useEffect(() => {
    setPendingStartDate(parseISO(committedStart))
    setPendingEndDate(parseISO(committedEnd))
  }, [committedStart, committedEnd])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [policies, setPolicies] = useState([])
  const [policiesLoading, setPoliciesLoading] = useState(false)
  const [sessions, setSessions] = useState([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  // Daily active/created session counts for the chart (org-scoped, same filter).
  const [sessionsByDayData, setSessionsByDayData] = useState([])
  // Negative-feedback turns for the org over the range (loaded with sessions).
  const [negativeFeedback, setNegativeFeedback] = useState([])
  // Ids of the expanded collapsible sections; all open by default.
  const [expandedSections, setExpandedSections] = useState([
    'sessions',
    'negative-feedback',
    'token-usage-policies',
  ])
  const handleToggleSection = (sectionId) =>
    setExpandedSections((prev) =>
      prev.includes(sectionId) ? prev.filter((item) => item !== sectionId) : [...prev, sectionId]
    )
  const [reloadKey, setReloadKey] = useState(0)
  // Drop stale responses when the org/date changes mid-flight. Separate refs so
  // the independent fetches don't invalidate each other.
  const requestIdRef = useRef(0)
  const policiesRequestIdRef = useRef(0)
  const sessionsRequestIdRef = useRef(0)

  useEffect(() => {
    dispatch(fetchOrganizations())
  }, [dispatch])

  useEffect(() => {
    if (!isNil(selectedOrgId)) {
      dispatch(fetchUsers({ organizationId: selectedOrgId }))
    }
  }, [dispatch, selectedOrgId])

  useEffect(() => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    const load = async () => {
      try {
        const data = await dispatch(
          fetchKodiUsageStats({
            startDate: committedStart,
            endDate: committedEnd,
            organizationId: selectedOrgId,
            timezone: browserTimezone,
          })
        )
        // Ignore all-but-latest responses so a slow earlier request can't
        // overwrite a newer filter's results.
        if (requestId === requestIdRef.current) {
          setStats(data)
        }
      } catch {
        if (requestId === requestIdRef.current) {
          setError({ kind: 'fetchFailed' })
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    }
    load()
  }, [dispatch, committedStart, committedEnd, selectedOrgId])

  // The daily chart runs for all orgs (no filter) and org-scoped; the sessions
  // listing is org-scoped only. The request id drops responses a newer filter
  // superseded (bumped on every run, so it also invalidates prior in-flight).
  useEffect(() => {
    const requestId = ++sessionsRequestIdRef.current
    setSessionsLoading(true)
    const params = {
      startDate: committedStart,
      endDate: committedEnd,
      organizationId: selectedOrgId,
      timezone: browserTimezone,
    }
    const load = async () => {
      // Settle both independently so one failing doesn't discard the other's
      // data. The sessions listing is skipped (and the table cleared) with no
      // org selected, since the all-orgs listing is unbounded.
      // Negative feedback, like the chart and cards, spans all orgs or one; only
      // the sessions listing is org-scoped (the all-orgs set is unbounded).
      const [byDayResult, sessionsResult, feedbackResult] = await Promise.allSettled([
        dispatch(fetchKodiUsageSessionsByDay(params)),
        isNil(selectedOrgId) ? Promise.resolve([]) : dispatch(fetchKodiUsageSessions(params)),
        dispatch(fetchKodiNegativeFeedback(params)),
      ])
      if (requestId !== sessionsRequestIdRef.current) return
      if (byDayResult.status === 'fulfilled') {
        setSessionsByDayData(byDayResult.value)
      } else {
        setSessionsByDayData([])
        toast.error('Could not load the sessions chart')
      }
      if (isNil(selectedOrgId)) {
        setSessions([])
      } else if (sessionsResult.status === 'fulfilled') {
        setSessions(sessionsResult.value)
      } else {
        setSessions([])
        toast.error('Could not load Kodi sessions')
      }
      if (feedbackResult.status === 'fulfilled') {
        setNegativeFeedback(feedbackResult.value)
      } else {
        setNegativeFeedback([])
        toast.error('Could not load negative feedback')
      }
      setSessionsLoading(false)
    }
    load()
  }, [dispatch, committedStart, committedEnd, selectedOrgId])

  useEffect(() => {
    if (isNil(selectedOrgId)) {
      setPolicies([])
      return
    }
    const requestId = ++policiesRequestIdRef.current
    setPoliciesLoading(true)
    const load = async () => {
      try {
        const data = await dispatch(fetchKodiUsagePolicies(selectedOrgId))
        if (requestId === policiesRequestIdRef.current) {
          setPolicies(data)
        }
      } catch {
        if (requestId === policiesRequestIdRef.current) {
          toast.error('Could not load usage policies')
        }
      } finally {
        if (requestId === policiesRequestIdRef.current) {
          setPoliciesLoading(false)
        }
      }
    }
    load()
  }, [dispatch, selectedOrgId, reloadKey])

  const organizationOptions = getOrganizationOptions(organizations)

  const handleDateRangeChange = ({ selection }) => {
    if (selection?.startDate && selection?.endDate) {
      setPendingStartDate(selection.startDate)
      setPendingEndDate(selection.endDate)
    }
  }

  const handleDateRangeClose = () => {
    if (differenceInCalendarDays(pendingEndDate, pendingStartDate) >= MAX_RANGE_DAYS) {
      toast.error(`Date range must be ${MAX_RANGE_DAYS} days or less`)
      setPendingStartDate(committedStartDate)
      setPendingEndDate(committedEndDate)
      return
    }
    history.replace({
      pathname: location.pathname,
      search: queryString.stringify({
        ...searchParams,
        from_date: format(pendingStartDate, 'yyyy-MM-dd'),
        to_date: format(pendingEndDate, 'yyyy-MM-dd'),
      }),
    })
  }

  const totalSessions = stats?.total_sessions ?? 0
  const averageTurns = totalSessions ? (stats.turns / totalSessions).toFixed(1) : '0'
  const averagePerSession = (totalMs = 0) =>
    formatSeconds(totalSessions ? totalMs / totalSessions / 1000 : 0)
  const cards = [
    { description: 'Total Sessions', data: totalSessions.toLocaleString() },
    { description: 'Total Users', data: (stats?.total_users ?? 0).toLocaleString() },
    // Org count only means something across all orgs; with a filter it's always 1.
    ...(isNil(selectedOrgId)
      ? [{ description: 'Total Orgs', data: (stats?.total_orgs ?? 0).toLocaleString() }]
      : []),
    { description: 'Input Tokens', data: (stats?.input_tokens ?? 0).toLocaleString() },
    { description: 'Output Tokens', data: (stats?.output_tokens ?? 0).toLocaleString() },
    { description: 'Avg Turns / Session', data: averageTurns },
    { description: 'Negative Feedback', data: (stats?.negative_feedbacks ?? 0).toLocaleString() },
    { description: 'Capability Gaps', data: (stats?.capability_gaps ?? 0).toLocaleString() },
    { description: 'Total Cost', data: `$${(stats?.total_cost ?? 0).toFixed(2)}` },
    {
      description: 'Avg Time to First Token / Session',
      data: averagePerSession(stats?.total_time_to_first_token_ms),
    },
    {
      description: 'Avg Time to First Answer / Session',
      data: averagePerSession(stats?.total_time_to_first_answer_ms),
    },
    {
      // total_duration_ms measures one turn, so its average is per turn.
      description: 'Avg Turn Duration',
      data: formatSeconds(stats?.turns ? stats.total_duration_ms / stats.turns / 1000 : 0),
    },
  ]

  const policyRows = policies.map((policy) => ({
    ...policy,
    scope: isNil(policy.user_id) ? 'All Users' : policy.user_name,
    percent: policyUsagePercent(policy),
  }))

  // The BE returns a dense, zero-filled series (one row per day in the range),
  // so this is just a label format. parseISO reads the date as local — new Date
  // on a bare 'yyyy-MM-dd' would parse UTC and shift a day in western zones.
  const sessionsByDay = sessionsByDayData.map((row) => ({
    date: format(parseISO(row.date), 'MMM d'),
    active: row.active_sessions,
    created: row.created_sessions,
  }))

  // Shown on both the all-orgs view and inside an org's Sessions section, so it's
  // defined once here and placed in both branches below.
  const sessionsByDayChart = (sessionsLoading || !isEmpty(sessionsByDayData)) && (
    <Card>
      <h4 className="kodi-usage-page__chart-title">Sessions by Day</h4>
      <EChart
        style={{ height: '260px' }}
        loading={sessionsLoading}
        option={{
          grid: { top: 40, right: 16, bottom: 48, left: 48 },
          legend: { top: 0 },
          tooltip: { trigger: 'axis' },
          xAxis: {
            type: 'category',
            data: sessionsByDay.map((day) => day.date),
            name: 'Date',
            nameLocation: 'middle',
            nameGap: 32,
          },
          yAxis: { type: 'value', name: 'Sessions', minInterval: 1 },
          series: [
            {
              name: 'Active',
              type: 'line',
              smooth: true,
              showSymbol: false,
              data: sessionsByDay.map((day) => day.active),
              lineStyle: { color: CHART.BRAND },
              itemStyle: { color: CHART.BRAND },
            },
            {
              name: 'Created',
              type: 'line',
              smooth: true,
              showSymbol: false,
              data: sessionsByDay.map((day) => day.created),
              lineStyle: { color: CHART.SUCCESS_HOVER },
              itemStyle: { color: CHART.SUCCESS_HOVER },
            },
          ],
        }}
      />
    </Card>
  )

  // Shown on both the all-orgs view and inside an org's sections. Spans all orgs
  // or one (the endpoint allows both).
  const negativeFeedbackSection = (
    <Accordion
      id="negative-feedback"
      title="Negative Feedback"
      className="kodi-usage-page__accordion"
      expanded={expandedSections.includes('negative-feedback')}
      handleToggle={handleToggleSection}
    >
      <Card className="no-padding">
        <AdvancedTable
          rows={negativeFeedback}
          loading={sessionsLoading}
          pagination
          index="audit_id"
          // Backend returns newest-first; keep that order (no created_at column to
          // sort by, and the default 'id' sort would scramble it).
          sortEnabled={false}
          columns={[
            { label: 'Username', accessor: 'username', isSearchable: true },
            { label: 'Organization', accessor: 'org_name', isSearchable: true },
            {
              label: 'Session Name',
              accessor: 'session_name',
              isSearchable: true,
              // Always links to the session with the audit_uuid param so the audit
              // drawer for this turn auto-opens (KodiAuditButton reads that param).
              // Unredacted-org rows link too, showing the "[Unredacted session]"
              // placeholder as the label — the session/drawer enforces support
              // access on click, so the user gets in or not; the real title is
              // never sent here, only the non-sensitive id/audit_id in the href.
              format: (sessionName, row) => (
                <a
                  href={`/kodi/${row.session_id}?audit_uuid=${row.audit_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {sessionName || 'Untitled session'}
                </a>
              ),
            },
            {
              label: 'UUID',
              accessor: 'session_id',
              isSearchable: true,
              format: sessionUuidCell,
            },
          ]}
          noDataText="No negative feedback in this date range"
        />
      </Card>
    </Accordion>
  )

  return (
    <div className="kodi-usage-page" data-testid="kodi-usage-page">
      <PageHeader title="Kodi Usage" />

      <FilterForm
        small
        sticky={false}
        inlineActions="end"
        actions={
          isUnredacted && (
            <InteractiveTooltip content="This organization is unredacted — session titles are hidden.">
              <span
                className="kodi-usage-page__unredacted-badge"
                data-testid="kodi-usage-unredacted-badge"
                aria-label="This organization is unredacted"
                // Focusable so keyboard users can open the tooltip (it opens on
                // focus and click, hence the interactive role).
                role="button"
                tabIndex={0}
              >
                <IconLock size={16} aria-hidden="true" />
                Unredacted
              </span>
            </InteractiveTooltip>
          )
        }
      >
        <FormField>
          <label htmlFor="kodi-usage-org-filter">Organization</label>
          <Select
            inputId="kodi-usage-org-filter"
            placeholder="All organizations"
            options={organizationOptions}
            value={selectedOrgId}
            // react-select passes the option, or null when cleared → all orgs.
            onChange={(option) => handleOrgChange(option?.value ?? null)}
            data-testid="kodi-usage-org-filter"
            isClearable
            search
          />
        </FormField>
        <FormField>
          <label>Date Range</label>
          <DateRangePickerReusable
            data-testid="kodi-usage-date-picker"
            onChange={handleDateRangeChange}
            onClose={handleDateRangeClose}
            startDate={pendingStartDate}
            endDate={pendingEndDate}
            friendlyLabel
            basicLabel
          />
        </FormField>
      </FilterForm>

      {error ? (
        <ErrorMessage content="Failed to load Kodi usage. Try a different filter or refresh." />
      ) : (
        <>
          <div className="analytics-card-grid" data-testid="kodi-usage-cards">
            {cards.map((card) => (
              <AnalyticsCard
                key={card.description}
                loading={loading}
                data={card.data}
                description={card.description}
                hideDateDescription
              />
            ))}
          </div>

          {isNil(selectedOrgId) ? (
            <div className="kodi-usage-page__sections">
              {sessionsByDayChart}
              {negativeFeedbackSection}
              <p className="kodi-usage-page__policies-hint">
                Select an organization to see its sessions and usage policies.
              </p>
            </div>
          ) : (
            <div className="kodi-usage-page__sections">
              <Accordion
                id="sessions"
                title="Sessions"
                className="kodi-usage-page__accordion"
                expanded={expandedSections.includes('sessions')}
                handleToggle={handleToggleSection}
              >
                {sessionsByDayChart}
                <Card className="no-padding">
                  <AdvancedTable
                    rows={sessions}
                    loading={sessionsLoading}
                    pagination
                    index="session_id"
                    defaultOrderBy="last_turn"
                    defaultOrder="desc"
                    columns={[
                      { label: 'Username', accessor: 'username', isSearchable: true },
                      {
                        label: 'Session Name',
                        accessor: 'name',
                        isSearchable: true,
                        // Always links to the session in a new tab via an anchor
                        // (not window.open) so the browser applies noopener and
                        // native new-tab behavior. Unredacted orgs link too,
                        // showing the "[Unredacted session]" placeholder as the
                        // label; the session enforces support access on click.
                        format: (name, row) => (
                          <a
                            href={`/kodi/${row.session_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {name || 'Untitled session'}
                          </a>
                        ),
                      },
                      {
                        label: 'UUID',
                        accessor: 'session_id',
                        isSearchable: true,
                        format: sessionUuidCell,
                      },
                      {
                        label: 'Deleted',
                        accessor: 'is_visible',
                        format: (isVisible) =>
                          isVisible ? (
                            <Pill xs success>
                              No
                            </Pill>
                          ) : (
                            <Pill xs critical>
                              Yes
                            </Pill>
                          ),
                      },
                      {
                        label: 'Total Cost',
                        accessor: 'total_cost',
                        // Hover breaks the cost down into turns and token counts.
                        format: (cost, row) => (
                          <InteractiveTooltip
                            content={
                              <>
                                <div>{(row.num_turns ?? 0).toLocaleString()} turn(s)</div>
                                <div>{(row.input_tokens ?? 0).toLocaleString()} input tokens</div>
                                <div>{(row.output_tokens ?? 0).toLocaleString()} output tokens</div>
                              </>
                            }
                          >
                            <span>{`$${(cost ?? 0).toFixed(2)}`}</span>
                          </InteractiveTooltip>
                        ),
                      },
                      {
                        label: 'Blocked',
                        accessor: 'blocked',
                        // Count of blocked turns; hover shows the per-reason
                        // breakdown. Plain 0 (no tooltip) when nothing was blocked.
                        format: (blocked, row) => {
                          const count = blocked ?? 0
                          const categories = Object.entries(row.blocked_by_category ?? {})
                          if (!count || isEmpty(categories)) {
                            return count.toLocaleString()
                          }
                          return (
                            <InteractiveTooltip
                              content={
                                <>
                                  {categories.map(([reason, n]) => (
                                    <div
                                      key={reason}
                                    >{`${capitalize(reason.replace(/_/g, ' '))}: ${n}`}</div>
                                  ))}
                                </>
                              }
                            >
                              {/* Focusable so keyboard users can open the tooltip
                                  (InteractiveTooltip opens on focus + hover). */}
                              <span
                                role="button"
                                tabIndex={0}
                                aria-label={`${count} blocked, breakdown`}
                              >
                                {count.toLocaleString()}
                              </span>
                            </InteractiveTooltip>
                          )
                        },
                      },
                      {
                        label: 'Capability Gap',
                        accessor: 'capability_gap_flagged',
                        format: (flagged) =>
                          flagged ? (
                            <Pill xs critical>
                              Yes
                            </Pill>
                          ) : (
                            <Pill xs success>
                              No
                            </Pill>
                          ),
                      },
                      { label: 'Created', accessor: 'created_at', format: dateCell },
                      { label: 'Last Turn', accessor: 'last_turn', format: dateCell },
                    ]}
                    noDataText="No sessions in this date range"
                  />
                </Card>
              </Accordion>

              {negativeFeedbackSection}

              <Accordion
                id="token-usage-policies"
                title="Token Usage Policies"
                className="kodi-usage-page__accordion"
                expanded={expandedSections.includes('token-usage-policies')}
                handleToggle={handleToggleSection}
              >
                <div className="kodi-usage-page__policies-actions">
                  <ButtonAndFormModal
                    modalId="kodiUsage/createPolicy"
                    buttonLabel="Create Usage Policy"
                    buttonProps={{ brand: true }}
                    modalTitle="Create Usage Policy"
                    form={
                      <CreateUsagePolicyForm
                        organizationId={selectedOrgId}
                        onSaved={() => setReloadKey((key) => key + 1)}
                      />
                    }
                  />
                </div>
                <Card className="no-padding">
                  <AdvancedTable
                    rows={policyRows}
                    loading={policiesLoading}
                    pagination
                    columns={[
                      {
                        label: 'Scope',
                        accessor: 'scope',
                        isSearchable: true,
                      },
                      {
                        label: 'Window',
                        accessor: 'window_hours',
                        format: formatPolicyWindow,
                      },
                      {
                        label: 'Status',
                        accessor: 'enabled',
                        format: (enabled) =>
                          enabled ? (
                            <Pill xs success>
                              Enabled
                            </Pill>
                          ) : (
                            <Pill xs critical>
                              Disabled
                            </Pill>
                          ),
                      },
                      {
                        label: 'Token Limit',
                        accessor: 'token_limit',
                        format: (limit) => limit.toLocaleString(),
                      },
                      {
                        label: 'Usage',
                        accessor: 'percent',
                        format: (percent, row) => (
                          <div className="kodi-usage-page__policy-usage">
                            <InteractiveTooltip
                              content={`${row.tokens_used.toLocaleString()} / ${row.token_limit.toLocaleString()} tokens`}
                            >
                              <Progress value={percent} />
                            </InteractiveTooltip>
                            <span>{Math.round(percent)}%</span>
                          </div>
                        ),
                      },
                      {
                        label: 'Actions',
                        accessor: 'id',
                        isSortable: false,
                        format: (id, row) => (
                          <ButtonAndFormModal
                            modalId={`kodiUsage/editPolicy/${id}`}
                            icon={<IconPencil />}
                            buttonProps={{ basic: true, square: true, 'aria-label': 'Edit policy' }}
                            modalTitle="Edit Usage Policy"
                            form={
                              <CreateUsagePolicyForm
                                organizationId={selectedOrgId}
                                policy={row}
                                onSaved={() => setReloadKey((key) => key + 1)}
                              />
                            }
                          />
                        ),
                      },
                    ]}
                    noDataText="No token usage policies"
                  />
                </Card>
              </Accordion>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default KodiUsagePage
